import { Fade } from '@material-ui/core'
import React, { useContext, useEffect, useState } from 'react'

import LoaderWrapper from '@flatsy/core/Loader/Wrapper'
import Failure from 'src/components/Failure'

import { FormContext, ReturnContextProps } from '../Context'
import { WizardContextState, WizardScreen } from '../Context/Type'
import Booking from './Booking'
import ClosedPage from './Closed'
import CoCandidat from './CoCandidat'
import HasGuarantor from './HasGuarantor'
import MainApplicant from './MainApplicant'
import NoBookableSlot from './NoBookableSlot'
import Professional from './Professional'
import ProjectPro from './Project/Pro'
import ProjectRent from './Project/Rent'
import ProjectSale from './Project/Sale'
import ShowSlotAlreadyTaken from './ResultScreen/SlotAlreadyTaken'
import Success from './ResultScreen/Success'
import { FormStepsProps } from './Type'
import VerlingueGuarantor from './VerlingueGuarantor'

const Steps: React.FC<FormStepsProps> = ({ renderMobileFormButton }) => {
  const { wizardContext, paramsUrl } = useContext(FormContext) as ReturnContextProps
  const params = paramsUrl.applicationId || paramsUrl.id || ''
  const assetId = wizardContext.context.asset?.id || ''

  const [fade, setFade] = useState('idle')
  const [controlledContext, setControlledContext] = useState<WizardContextState>(wizardContext)

  useEffect(() => {
    if (fade !== 'idle') {
      setFade('hide')
      setTimeout(() => {
        setFade('show')
        setControlledContext(wizardContext)
      }, 350)
    } else {
      setFade('show')
      setControlledContext(wizardContext)
    }
  }, [wizardContext.value])

  const renderStep = () => {
    /* -------------------------------------------------------------------------- */
    /*                                   BOOKING                                  */
    /* -------------------------------------------------------------------------- */
    if (controlledContext.matches(WizardScreen.bookingSelector)) {
      return (
        <Booking
          withOutApplication={!paramsUrl.applicationId}
          assetId={assetId}
          paramsUrl={params}
          renderMobileFormButton={renderMobileFormButton}
        />
      )
    }

    /* -------------------------------------------------------------------------- */
    /*                                 NO BOOKING                                 */
    /* -------------------------------------------------------------------------- */
    if (controlledContext.matches(WizardScreen.noBookableSlot)) {
      return <NoBookableSlot paramsUrl={paramsUrl.id || ''} />
    }

    /* -------------------------------------------------------------------------- */
    /*                                   CLOSED                                   */
    /* -------------------------------------------------------------------------- */
    if (controlledContext.matches(WizardScreen.closed)) {
      return <ClosedPage />
    }

    /* -------------------------------------------------------------------------- */
    /*                                PROJECT RENT                                */
    /* -------------------------------------------------------------------------- */
    if (controlledContext.matches(WizardScreen.projectRent)) {
      return (
        <ProjectRent
          withOutApplication={!paramsUrl.applicationId}
          assetId={assetId}
          paramsUrl={params}
          renderMobileFormButton={renderMobileFormButton}
        />
      )
    }

    /* -------------------------------------------------------------------------- */
    /*                                PROJECT SALE                                */
    /* -------------------------------------------------------------------------- */
    if (controlledContext.matches(WizardScreen.projectSale)) {
      return (
        <ProjectSale
          withOutApplication={!paramsUrl.applicationId}
          assetId={assetId}
          paramsUrl={params}
          renderMobileFormButton={renderMobileFormButton}
        />
      )
    }

    /* -------------------------------------------------------------------------- */
    /*                                PROJECT PRO                                 */
    /* -------------------------------------------------------------------------- */
    if (controlledContext.matches(WizardScreen.projectPro)) {
      return (
        <ProjectPro
          withOutApplication={!paramsUrl.applicationId}
          assetId={assetId}
          paramsUrl={params}
          renderMobileFormButton={renderMobileFormButton}
        />
      )
    }

    /* -------------------------------------------------------------------------- */
    /*                               MAIN APPLICANT                               */
    /* -------------------------------------------------------------------------- */
    if (controlledContext.matches(WizardScreen.applicantMain)) {
      return (
        <MainApplicant
          withOutApplication={!paramsUrl.applicationId}
          assetId={assetId}
          paramsUrl={params}
          renderMobileFormButton={renderMobileFormButton}
        />
      )
    }

    /* -------------------------------------------------------------------------- */
    /*                                 CO CANDIDAT                                */
    /* -------------------------------------------------------------------------- */
    if (controlledContext.matches(WizardScreen.applicantCoCandidat)) {
      return (
        <CoCandidat
          assetId={assetId}
          paramsUrl={params}
          renderMobileFormButton={renderMobileFormButton}
        />
      )
    }

    /* -------------------------------------------------------------------------- */
    /*                                PROFESSIONAL                                */
    /* -------------------------------------------------------------------------- */
    if (controlledContext.matches(WizardScreen.professional)) {
      return (
        <Professional
          assetId={assetId}
          paramsUrl={params}
          renderMobileFormButton={renderMobileFormButton}
        />
      )
    }

    /* -------------------------------------------------------------------------- */
    /*                                VERLINGUE GUARANTOR                         */
    /* -------------------------------------------------------------------------- */
    if (controlledContext.matches(WizardScreen.verlingueGuarantor)) {
      return (
        <VerlingueGuarantor
          assetId={assetId}
          paramsUrl={params}
          renderMobileFormButton={renderMobileFormButton}
        />
      )
    }

    /* -------------------------------------------------------------------------- */
    /*                                HAS GUARANTOR                               */
    /* -------------------------------------------------------------------------- */
    if (controlledContext.matches(WizardScreen.needGuarantor)) {
      return (
        <HasGuarantor
          assetId={assetId}
          paramsUrl={params}
          renderMobileFormButton={renderMobileFormButton}
        />
      )
    }

    /* -------------------------------------------------------------------------- */
    /*                                  GUARANTOR                                 */
    /* -------------------------------------------------------------------------- */
    if (controlledContext.matches(WizardScreen.guarantor)) {
      return (
        <Professional
          assetId={assetId}
          paramsUrl={params}
          isGuarantor
          renderMobileFormButton={renderMobileFormButton}
        />
      )
    }

    /* -------------------------------------------------------------------------- */
    /*                                   SUCCESS                                  */
    /* -------------------------------------------------------------------------- */
    if (controlledContext.matches(WizardScreen.success)) {
      return <Success />
    }

    /* -------------------------------------------------------------------------- */
    /*                                   FAILED                                   */
    /* -------------------------------------------------------------------------- */
    if (controlledContext.matches(WizardScreen.failed)) {
      return <Failure />
    }

    /* -------------------------------------------------------------------------- */
    /*                             SLOT ALREADY TAKEN                             */
    /* -------------------------------------------------------------------------- */
    if (controlledContext.matches(WizardScreen.slotAlreadyTaken)) {
      return <ShowSlotAlreadyTaken />
    }

    return <LoaderWrapper absolute />
  }

  return (
    <Fade
      in={fade === 'idle' || fade === 'show'}
      timeout={{
        enter: 800,
        exit: 300,
      }}
    >
      <div>{renderStep()}</div>
    </Fade>
  )
}

export default Steps
